<template>
  <div class="associates-page">
    <page-header
      eyebrow="Operação"
      title="Associados"
      subtitle="Gerencie cadastros, cargos e status com uma interface mais organizada e confortável para o dia a dia."
    >
      <template #actions>
        <v-btn-toggle
          v-model="viewMode"
          mandatory
          dense
          class="associates-page__view-toggle mr-3"
          @change="onViewModeChange"
        >
          <v-btn value="list" small>
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            Lista
          </v-btn>
          <v-btn value="network" small>
            <v-icon left small>mdi-file-tree</v-icon>
            Rede
          </v-btn>
          <v-btn value="history" small>
            <v-icon left small>mdi-history</v-icon>
            Histórico
          </v-btn>
        </v-btn-toggle>
        <v-btn color="primary" dark large elevation="0" @click="openDialog">
          <v-icon left>mdi-account-plus-outline</v-icon>
          Novo associado
        </v-btn>
      </template>
    </page-header>

    <v-skeleton-loader
      v-if="loading"
      tile
      loading
      class="mx-auto"
      type="card"
      boilerplate
    />

    <v-alert
      v-else-if="error"
      type="error"
      prominent
      class="mb-4"
      border="left"
    >
      <v-row align="center">
        <v-col class="grow">
          {{ errorMessage }}
        </v-col>
        <v-col class="shrink">
          <v-btn color="primary" @click="loadData"> Tentar novamente </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <template v-else-if="viewMode === 'network'">
      <v-row class="mb-4" dense>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-4 text-center metric-card">
            <div class="metric-card__label">Total na rede</div>
            <div class="metric-card__value">{{ network?.totals.total ?? 0 }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-4 text-center metric-card">
            <div class="metric-card__label">Premium</div>
            <div class="metric-card__value metric-card__value--premium">
              {{ network?.totals.premium ?? 0 }}
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-4 text-center metric-card">
            <div class="metric-card__label">Não Premium</div>
            <div class="metric-card__value">{{ network?.totals.nonPremium ?? 0 }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-4 text-center metric-card">
            <div class="metric-card__label">Exibindo</div>
            <div class="metric-card__value">
              {{ filteredNetworkMembers.length }} / {{ network?.totals.total ?? 0 }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="associates-page__table-card mb-4">
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="networkSearch"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                label="Buscar na rede"
                clearable
                placeholder="Nome, e-mail, documento..."
              />
            </v-col>
            <v-col cols="6" md="2">
              <v-select
                v-model="networkFilters.premiumStatus"
                :items="premiumStatusOptions"
                label="Premium"
                outlined
                dense
                hide-details
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="6" md="2">
              <v-select
                v-model="networkFilters.level"
                :items="levelOptions"
                label="Nível"
                outlined
                dense
                hide-details
                clearable
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field
                v-model="networkFilters.periodStart"
                label="De"
                type="date"
                outlined
                dense
                hide-details
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field
                v-model="networkFilters.periodEnd"
                label="Até"
                type="date"
                outlined
                dense
                hide-details
                @change="loadNetwork"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card class="associates-page__table-card">
        <v-tabs v-model="networkTab" background-color="transparent" color="primary">
          <v-tab>
            <v-icon left small>mdi-file-tree</v-icon>
            Árvore hierárquica
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            Lista completa
          </v-tab>
        </v-tabs>
        <v-divider />

        <v-tabs-items v-model="networkTab">
          <v-tab-item>
            <v-card-text>
              <div v-if="!network?.tree.length" class="text-center py-8 grey--text">
                Nenhum associado encontrado na rede.
              </div>
              <network-tree-node
                v-for="node in filteredNetworkTree"
                :key="node.id"
                :node="node"
              />
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-data-table
              :headers="networkHeaders"
              :items="filteredNetworkMembers"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-text': 'associados por página' }"
              no-data-text="Nenhum associado na rede"
              class="elevation-0 associates-page__table"
            >
              <template v-slot:body="{ items }">
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td><v-chip x-small outlined>N{{ item.level }}</v-chip></td>
                    <td>{{ item.name }}</td>
                    <td>{{ formatDocument(item.document) }}</td>
                    <td>{{ item.email }}</td>
                    <td>{{ phoneNumberFormat(item.phone) }}</td>
                    <td>
                      <v-chip
                        x-small
                        :color="item.isPremiumActive ? 'green darken-2' : 'grey'"
                        dark
                      >
                        {{ item.isPremiumActive ? "Premium" : "Não Premium" }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip small :color="statusColor(item.status)" dark class="app-chip">
                        {{ statusLabel(item.status) }}
                      </v-chip>
                    </td>
                    <td>{{ item.pagbankConnected ? "Sim" : "Não" }}</td>
                    <td>{{ formatDate(item.createdAt) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </template>

    <v-card v-else-if="viewMode === 'history'" class="associates-page__table-card">
      <v-card-title class="pb-0">
        <span class="text-h6">Histórico de vouchers Premium</span>
      </v-card-title>
      <v-data-table
        :headers="historyHeaders"
        :items="voucherHistory"
        :items-per-page="15"
        :footer-props="{ 'items-per-page-text': 'registros por página' }"
        no-data-text="Nenhum registro de voucher encontrado"
        class="elevation-0 associates-page__table"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ formatDate(item.createdAt) }}</td>
              <td>{{ item.operationLabel }}</td>
              <td>{{ item.beneficiaryName }}</td>
              <td>{{ formatDocument(item.beneficiaryDocument) }}</td>
              <td>{{ item.grantorName }}</td>
              <td class="text-center">{{ item.quantity }}</td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>

    <v-card v-else class="associates-page__table-card">
      <v-card-title class="pb-0">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              outlined
              dense
              hide-details
              append-icon="mdi-magnify"
              label="Pesquise por nome, e-mail, documento, cargo ou status"
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedCareerRole"
              :items="careerRoleOptions"
              item-text="text"
              item-value="value"
              label="Filtrar por cargo"
              clearable
              hide-details
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedVoucherFilter"
              :items="voucherFilterOptions"
              item-text="text"
              item-value="value"
              label="Vouchers"
              hide-details
              outlined
              dense
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        :header-props="{ sortByText: 'Filtrar por' }"
        :headers="headers"
        :items="filteredAssociates"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-text': 'associados por página' }"
        :search="search"
        no-data-text="Nenhum associado cadastrado"
        class="elevation-0 associates-page__table"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="d-block d-sm-table-cell">{{ item.name }}</td>
              <td class="d-block d-sm-table-cell">{{ formatDocument(item.document) }}</td>
              <td class="d-block d-sm-table-cell">{{ item.email }}</td>
              <td class="d-block d-sm-table-cell">{{ phoneNumberFormat(item.phone) }}</td>
              <td class="d-block d-sm-table-cell">
                <v-chip small :color="careerRoleColor(item)" dark class="app-chip">
                  {{ careerRoleLabel(item) }}
                </v-chip>
              </td>
              <td class="d-block d-sm-table-cell">
                <v-chip small :color="statusColor(item.status)" dark class="app-chip">
                  {{ statusLabel(item.status) }}
                </v-chip>
              </td>
              <td class="d-block d-sm-table-cell">
                <v-chip
                  v-if="item.hasPremiumVoucher"
                  x-small
                  color="deep-purple lighten-5"
                  text-color="deep-purple darken-2"
                >
                  {{ item.premiumVoucherBalance ?? 0 }} voucher(s)
                </v-chip>
                <span v-else class="grey--text caption">—</span>
              </td>
              <td class="d-block d-sm-table-cell">{{ formatDate(item.createdAt) }}</td>
              <td class="d-block d-sm-table-cell actions">
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      color="deep-purple darken-2"
                      class="associates-page__action"
                      v-bind="attrs"
                      v-on="on"
                      @click="openVoucherDialog(item)"
                    >
                      Vouchers
                    </v-btn>
                  </template>
                  <span>Gerenciar vouchers Premium do associado</span>
                </v-tooltip>
                <v-tooltip v-if="canApprove(item)" top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      color="green darken-2"
                      class="associates-page__action"
                      v-bind="attrs"
                      v-on="on"
                      @click="openConfirmStatus(item, 'ACTIVE')"
                    >
                      Aprovar
                    </v-btn>
                  </template>
                  <span>Libera o acesso ativo ao painel da franquia</span>
                </v-tooltip>
                <v-tooltip v-if="canInactivate(item)" top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      color="grey darken-2"
                      class="associates-page__action"
                      v-bind="attrs"
                      v-on="on"
                      @click="openConfirmStatus(item, 'INACTIVE')"
                    >
                      Inativar
                    </v-btn>
                  </template>
                  <span>Somente visualização no painel da franquia</span>
                </v-tooltip>
                <v-tooltip v-else-if="canActivate(item)" top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      color="green darken-2"
                      class="associates-page__action"
                      v-bind="attrs"
                      v-on="on"
                      @click="openConfirmStatus(item, 'ACTIVE')"
                    >
                      Ativar
                    </v-btn>
                  </template>
                  <span>Volta a aparecer no painel da franquia</span>
                </v-tooltip>
                <span
                  v-if="!canApprove(item) && !canInactivate(item) && !canActivate(item)"
                  class="text--disabled caption"
                >
                  —
                </span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogOpen" max-width="560" persistent>
      <v-card class="associates-page__dialog-card">
        <v-card-title class="headline pb-0">Novo associado</v-card-title>
        <v-card-text>
          <p class="caption mb-2 grey--text text--darken-1">
            Sem vínculo com franquia. Se o associado ainda não tiver conta PagBank, o split
            correspondente vai para a BRPoints.
          </p>
          <v-alert type="info" dense outlined class="mb-4">
            Todo novo cadastro entra como <strong>Associado</strong>, com split fixo de
            <strong>0,10%</strong> no Pix e <strong>0,05%</strong> no Cartão. Os demais cargos
            são alcançados por conquistas no plano de carreira.
          </v-alert>
          <v-form ref="formRef" v-model="formValid">
            <v-text-field
              v-model="form.name"
              label="Nome *"
              outlined
              dense
              :rules="[rules.required]"
            />
            <v-text-field
              v-model="form.document"
              label="CPF/CNPJ *"
              outlined
              dense
              :rules="[rules.required]"
            />
            <v-text-field
              v-model="form.email"
              label="E-mail *"
              outlined
              dense
              type="email"
              :rules="[rules.required, rules.email]"
            />
            <v-text-field
              v-model="form.phone"
              label="Telefone *"
              outlined
              dense
              :rules="[rules.required]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" dark :loading="saving" @click="submitForm">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="voucherDialogOpen"
      max-width="820"
      persistent
      content-class="voucher-modal-dialog"
    >
      <v-card v-if="voucherTarget" class="voucher-modal" elevation="0">
        <div class="voucher-modal__hero">
          <div class="voucher-modal__hero-left">
            <div class="voucher-modal__hero-icon">
              <v-icon color="white" size="22">mdi-ticket-percent</v-icon>
            </div>
            <div>
              <h2 class="voucher-modal__title">Vouchers Premium</h2>
              <p class="voucher-modal__subtitle">
                Conceda vouchers para distribuição gratuita de assinaturas Premium.
              </p>
            </div>
          </div>
          <v-btn icon dark class="voucher-modal__close" @click="closeVoucherDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="voucher-modal__body">
          <div class="voucher-modal__associate">
            <v-avatar color="deep-purple lighten-5" size="44" class="mr-3">
              <span class="voucher-modal__avatar-text">{{ voucherAssociateInitials }}</span>
            </v-avatar>
            <div class="voucher-modal__associate-info">
              <div class="voucher-modal__associate-name">{{ voucherTarget.name }}</div>
              <div class="voucher-modal__associate-meta">
                <span>{{ formatDocument(voucherTarget.document) }}</span>
                <span>{{ voucherTarget.email }}</span>
              </div>
            </div>
            <v-chip
              small
              :color="voucherTarget.isPremiumActive ? 'green darken-2' : 'grey lighten-1'"
              :text-color="voucherTarget.isPremiumActive ? 'white' : 'grey darken-3'"
              class="flex-shrink-0"
            >
              {{ voucherTarget.isPremiumActive ? "Premium" : "Não Premium" }}
            </v-chip>
          </div>

          <div
            v-if="voucherWillAutoUpgrade || voucherGrantDelta > 0"
            class="voucher-modal__notice"
            :class="{
              'voucher-modal__notice--warning': voucherWillAutoUpgrade,
              'voucher-modal__notice--success': !voucherWillAutoUpgrade && voucherGrantDelta > 0,
            }"
          >
            <v-icon small class="mr-2">
              {{ voucherWillAutoUpgrade ? "mdi-star-shooting" : "mdi-gift-outline" }}
            </v-icon>
            <span v-if="voucherWillAutoUpgrade">
              Ao ativar, o associado vira <strong>Premium</strong> sem comissões para a rede.
            </span>
            <span v-else>
              Serão liberados <strong>{{ voucherGrantDelta }}</strong>
              {{ voucherGrantDelta === 1 ? "novo voucher" : "novos vouchers" }}.
            </span>
          </div>

          <div class="voucher-modal__controls">
            <div class="voucher-modal__section voucher-modal__section--toggle">
              <div class="voucher-modal__section-header">
                <div>
                  <div class="voucher-modal__section-title">Distribuição ativa</div>
                  <div class="voucher-modal__section-hint">
                    Permite conceder vouchers na rede.
                  </div>
                </div>
                <v-switch
                  v-model="voucherForm.hasPremiumVoucher"
                  color="deep-purple darken-2"
                  hide-details
                  class="mt-0 pt-0"
                  @change="onVoucherToggleChange"
                />
              </div>
            </div>

            <div
              class="voucher-modal__section voucher-modal__section--balance"
              :class="{ 'voucher-modal__section--balance-inactive': !voucherForm.hasPremiumVoucher }"
            >
              <div class="voucher-modal__section-title">Saldo disponível</div>
              <div class="voucher-modal__section-hint">
                Cada uso consome 1 voucher.
              </div>

              <template v-if="voucherForm.hasPremiumVoucher">
                <div class="voucher-modal__balance-control">
                  <v-btn
                    icon
                    small
                    outlined
                    color="deep-purple darken-2"
                    :disabled="voucherForm.premiumVoucherBalance <= 0"
                    @click="adjustVoucherBalance(-1)"
                  >
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>

                  <v-text-field
                    v-model.number="voucherForm.premiumVoucherBalance"
                    type="number"
                    min="0"
                    step="1"
                    outlined
                    dense
                    hide-details
                    class="voucher-modal__balance-input"
                    :rules="[rules.nonNegativeInteger]"
                    @keydown="preventInvalidVoucherKeys"
                  />

                  <v-btn
                    icon
                    small
                    outlined
                    color="deep-purple darken-2"
                    @click="adjustVoucherBalance(1)"
                  >
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </div>

                <div class="voucher-modal__balance-summary">
                  Saldo: <strong>{{ originalVoucherBalance }}</strong>
                  <template v-if="voucherBalanceChanged">
                    → <strong>{{ voucherForm.premiumVoucherBalance }}</strong>
                  </template>
                </div>
              </template>
              <p v-else class="voucher-modal__balance-placeholder">
                Ative a distribuição para definir o saldo.
              </p>
            </div>
          </div>

          <p class="voucher-modal__footnote mb-0">
            Vouchers geram Premium gratuito — sem comissão, bônus ou bonificação para a rede.
          </p>
        </v-card-text>

        <v-divider />

        <v-card-actions class="voucher-modal__actions">
          <v-btn text :disabled="savingVouchers" @click="closeVoucherDialog">
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="deep-purple darken-2"
            dark
            depressed
            :loading="savingVouchers"
            :disabled="!voucherFormChanged"
            @click="submitVoucherForm"
          >
            Salvar alterações
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmStatusOpen" max-width="480">
      <v-card class="associates-page__dialog-card">
        <v-card-title class="headline">{{ confirmStatusDialogTitle }}</v-card-title>
        <v-card-text>
          <p class="mb-0">
            Confirma a ação para <strong>{{ pendingStatusChange?.name }}</strong>?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmStatusOpen = false">Cancelar</v-btn>
          <v-btn
            :color="pendingStatus === 'ACTIVE' ? 'green darken-2' : 'grey darken-3'"
            dark
            :loading="updatingStatus"
            @click="confirmStatusChange"
          >
            {{ confirmStatusActionLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { format } from "date-fns";
import PageHeader from "@/components/pageHeader/PageHeader.vue";
import NetworkTreeNode from "@/components/franchise-associates/NetworkTreeNode.vue";
import { FranchiseAssociateListItem } from "@/types/franchise-associate/FranchiseAssociate";
import {
  AssociateNetworkFilters,
  AssociateNetworkNode,
  AssociateNetworkResponse,
} from "@/types/franchise-associate/AssociateNetwork";
import { ListFranchiseAssociates } from "@/services/franchise-associates/ListFranchiseAssociates";
import { GetAssociateNetwork } from "@/services/franchise-associates/GetAssociateNetwork";
import { RegisterFranchiseAssociate } from "@/services/franchise-associates/RegisterFranchiseAssociate";
import { UpdateFranchiseAssociateStatus } from "@/services/franchise-associates/UpdateFranchiseAssociateStatus";
import { UpdateFranchiseAssociateVouchers } from "@/services/franchise-associates/UpdateFranchiseAssociateVouchers";
import { GetPremiumVoucherHistory } from "@/services/franchise-associates/GetPremiumVoucherHistory";
import { PremiumVoucherHistoryItem } from "@/types/franchise-associate/PremiumVoucherHistory";

export default Vue.extend({
  name: "FranchiseAssociatesView",
  components: { PageHeader, NetworkTreeNode },

  data() {
    return {
      loading: true,
      error: false,
      errorMessage: "",
      viewMode: "list" as "list" | "network" | "history",
      voucherHistory: [] as PremiumVoucherHistoryItem[],
      voucherDialogOpen: false,
      voucherTarget: null as FranchiseAssociateListItem | null,
      savingVouchers: false,
      voucherForm: {
        hasPremiumVoucher: false,
        premiumVoucherBalance: 0,
      },
      originalVoucherBalance: 0,
      originalHasPremiumVoucher: false,
      associates: [] as FranchiseAssociateListItem[],
      network: null as AssociateNetworkResponse | null,
      networkTab: 0,
      networkSearch: "",
      networkFilters: {
        premiumStatus: "ALL",
        level: null,
        periodStart: "",
        periodEnd: "",
      } as AssociateNetworkFilters,
      premiumStatusOptions: [
        { text: "Todos", value: "ALL" },
        { text: "Premium", value: "PREMIUM" },
        { text: "Não Premium", value: "NON_PREMIUM" },
      ],
      levelOptions: [
        { text: "Nível 1", value: 1 },
        { text: "Nível 2", value: 2 },
        { text: "Nível 3", value: 3 },
        { text: "Nível 4", value: 4 },
        { text: "Nível 5", value: 5 },
      ],
      search: "",
      selectedCareerRole: null as string | null,
      selectedVoucherFilter: "ALL" as "ALL" | "WITH_VOUCHER" | "WITH_BALANCE" | "WITHOUT_VOUCHER",
      dialogOpen: false,
      formValid: false,
      saving: false,
      confirmStatusOpen: false,
      pendingStatusChange: null as FranchiseAssociateListItem | null,
      pendingStatus: null as "ACTIVE" | "INACTIVE" | null,
      updatingStatus: false,
      form: {
        name: "",
        document: "",
        email: "",
        phone: "",
      },
      rules: {
        required: (v: string) => !!v || "Campo obrigatório",
        email: (v: string) =>
          /.+@.+\..+/.test(v) || "Informe um e-mail válido",
        nonNegativeInteger: (v: number) =>
          (Number.isInteger(Number(v)) && Number(v) >= 0) ||
          "Informe um número inteiro maior ou igual a zero",
      },
      careerRoleOptions: [
        { text: "Associado", value: "ASSOCIATE" },
        { text: "Gestor de Associados", value: "ASSOCIATE_MANAGER" },
        { text: "Gerente de Expansão", value: "EXPANSION_MANAGER" },
        { text: "Diretor de Expansão", value: "EXPANSION_DIRECTOR" },
        { text: "Sem cargo definido", value: "UNCLASSIFIED" },
      ],
      headers: [
        { text: "Nome", value: "name" },
        { text: "Documento", value: "document" },
        { text: "E-mail", value: "email" },
        { text: "Telefone", value: "phone" },
        { text: "Cargo", value: "careerRoleLabel" },
        { text: "Status", value: "status" },
        { text: "Vouchers", value: "premiumVoucherBalance", sortable: false },
        { text: "Cadastro", value: "createdAt" },
        { text: "Ações", value: "actions", sortable: false },
      ],
      historyHeaders: [
        { text: "Data", value: "createdAt" },
        { text: "Operação", value: "operationLabel" },
        { text: "Beneficiário", value: "beneficiaryName" },
        { text: "Documento", value: "beneficiaryDocument" },
        { text: "Concedente", value: "grantorName" },
        { text: "Qtd.", value: "quantity", align: "center" },
      ],
      networkHeaders: [
        { text: "Nível", value: "level" },
        { text: "Nome", value: "name" },
        { text: "Documento", value: "document" },
        { text: "E-mail", value: "email" },
        { text: "Telefone", value: "phone" },
        { text: "Premium", value: "isPremiumActive" },
        { text: "Status", value: "status" },
        { text: "PagBank", value: "pagbankConnected" },
        { text: "Cadastro", value: "createdAt" },
      ],
    };
  },

  mounted() {
    void this.loadData();
  },

  computed: {
    voucherFilterOptions(): Array<{ text: string; value: string }> {
      const withVoucher = this.associates.filter((item) => item.hasPremiumVoucher).length;
      const withBalance = this.associates.filter(
        (item) => item.hasPremiumVoucher && (item.premiumVoucherBalance ?? 0) > 0
      ).length;
      const withoutVoucher = this.associates.length - withVoucher;

      return [
        { text: `Todos (${this.associates.length})`, value: "ALL" },
        { text: `Possui voucher (${withVoucher})`, value: "WITH_VOUCHER" },
        { text: `Com saldo disponível (${withBalance})`, value: "WITH_BALANCE" },
        { text: `Sem voucher (${withoutVoucher})`, value: "WITHOUT_VOUCHER" },
      ];
    },
    filteredAssociates(): FranchiseAssociateListItem[] {
      return this.associates.filter((item) => {
        if (
          this.selectedCareerRole &&
          this.resolveCareerRole(item) !== this.selectedCareerRole
        ) {
          return false;
        }

        if (this.selectedVoucherFilter === "WITH_VOUCHER") {
          return !!item.hasPremiumVoucher;
        }
        if (this.selectedVoucherFilter === "WITH_BALANCE") {
          return !!item.hasPremiumVoucher && (item.premiumVoucherBalance ?? 0) > 0;
        }
        if (this.selectedVoucherFilter === "WITHOUT_VOUCHER") {
          return !item.hasPremiumVoucher;
        }

        return true;
      });
    },
    filteredNetworkMembers(): AssociateNetworkNode[] {
      const members = this.network?.members ?? [];
      const term = this.networkSearch.trim().toLowerCase();
      if (!term) return members;
      return members.filter((member) => {
        const haystack = [member.name, member.email, member.document]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    filteredNetworkTree(): AssociateNetworkNode[] {
      const term = this.networkSearch.trim().toLowerCase();
      if (!term || !this.network?.tree) {
        return this.network?.tree ?? [];
      }
      const allowedIds = new Set(this.filteredNetworkMembers.map((member) => member.id));
      const prune = (nodes: AssociateNetworkNode[]): AssociateNetworkNode[] =>
        nodes
          .map((node) => ({
            ...node,
            children: prune(node.children ?? []),
          }))
          .filter(
            (node) =>
              allowedIds.has(node.id) || (node.children && node.children.length > 0)
          );
      return prune(this.network?.tree ?? []);
    },
    confirmStatusActionLabel(): string {
      if (this.pendingStatus === "ACTIVE") {
        return this.pendingStatusChange?.status === "PENDING" ? "Aprovar" : "Ativar";
      }
      return "Inativar";
    },
    confirmStatusDialogTitle(): string {
      return `${this.confirmStatusActionLabel} associado`;
    },
    voucherAssociateInitials(): string {
      const name = this.voucherTarget?.name?.trim() ?? "";
      if (!name) return "?";
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    },
    voucherGrantDelta(): number {
      if (!this.voucherForm.hasPremiumVoucher) return 0;
      const balance = Number(this.voucherForm.premiumVoucherBalance);
      if (!Number.isFinite(balance)) return 0;
      return Math.max(0, balance - this.originalVoucherBalance);
    },
    voucherWillAutoUpgrade(): boolean {
      if (!this.voucherTarget || this.voucherTarget.isPremiumActive) return false;
      return this.voucherForm.hasPremiumVoucher && !this.originalHasPremiumVoucher;
    },
    voucherBalanceChanged(): boolean {
      return (
        Number(this.voucherForm.premiumVoucherBalance) !== this.originalVoucherBalance
      );
    },
    voucherFormChanged(): boolean {
      return (
        this.voucherForm.hasPremiumVoucher !== this.originalHasPremiumVoucher ||
        this.voucherBalanceChanged
      );
    },
  },

  methods: {
    async loadData() {
      if (this.viewMode === "network") {
        await this.loadNetwork();
        return;
      }
      if (this.viewMode === "history") {
        await this.loadVoucherHistory();
        return;
      }
      await this.loadList();
    },

    async onViewModeChange() {
      await this.loadData();
    },

    canApprove(item: FranchiseAssociateListItem): boolean {
      return item.status === "PENDING";
    },

    async loadList() {
      this.loading = true;
      this.error = false;
      this.errorMessage = "";
      try {
        const svc = new ListFranchiseAssociates();
        this.associates = await svc.execute();
      } catch (err: unknown) {
        this.error = true;
        const axiosErr = err as { response?: { status?: number } };
        const status = axiosErr?.response?.status;
        if (status === 401) {
          this.errorMessage =
            "Sessão expirada ou não autorizada. Faça login novamente.";
        } else {
          this.errorMessage =
            "Não foi possível carregar os associados. Tente novamente.";
        }
      } finally {
        this.loading = false;
      }
    },

    async loadVoucherHistory() {
      this.loading = true;
      this.error = false;
      this.errorMessage = "";
      try {
        this.voucherHistory = await new GetPremiumVoucherHistory().execute();
      } catch (err: unknown) {
        this.error = true;
        const axiosErr = err as { response?: { status?: number } };
        const status = axiosErr?.response?.status;
        if (status === 401) {
          this.errorMessage =
            "Sessão expirada ou não autorizada. Faça login novamente.";
        } else {
          this.errorMessage =
            "Não foi possível carregar o histórico de vouchers. Tente novamente.";
        }
      } finally {
        this.loading = false;
      }
    },

    async loadNetwork() {
      this.loading = true;
      this.error = false;
      this.errorMessage = "";
      try {
        const svc = new GetAssociateNetwork();
        this.network = await svc.execute({
          premiumStatus: this.networkFilters.premiumStatus,
          level: this.networkFilters.level || undefined,
          periodStart: this.networkFilters.periodStart || undefined,
          periodEnd: this.networkFilters.periodEnd || undefined,
        });
      } catch (err: unknown) {
        this.error = true;
        const axiosErr = err as { response?: { status?: number } };
        const status = axiosErr?.response?.status;
        if (status === 401) {
          this.errorMessage =
            "Sessão expirada ou não autorizada. Faça login novamente.";
        } else {
          this.errorMessage =
            "Não foi possível carregar a rede de associados. Tente novamente.";
        }
      } finally {
        this.loading = false;
      }
    },

    openVoucherDialog(item: FranchiseAssociateListItem) {
      this.voucherTarget = item;
      this.originalHasPremiumVoucher = !!item.hasPremiumVoucher;
      this.originalVoucherBalance = item.premiumVoucherBalance ?? 0;
      this.voucherForm = {
        hasPremiumVoucher: !!item.hasPremiumVoucher,
        premiumVoucherBalance: item.premiumVoucherBalance ?? 0,
      };
      this.voucherDialogOpen = true;
    },

    onVoucherToggleChange(enabled: boolean) {
      if (!enabled) {
        this.voucherForm.premiumVoucherBalance = 0;
      } else if (this.originalHasPremiumVoucher) {
        this.voucherForm.premiumVoucherBalance = this.originalVoucherBalance;
      }
    },

    adjustVoucherBalance(delta: number) {
      const next = Math.max(0, Number(this.voucherForm.premiumVoucherBalance) + delta);
      this.voucherForm.premiumVoucherBalance = next;
    },

    preventInvalidVoucherKeys(event: KeyboardEvent) {
      if (["e", "E", "+", "-", "."].includes(event.key)) {
        event.preventDefault();
      }
    },

    closeVoucherDialog() {
      this.voucherDialogOpen = false;
      this.voucherTarget = null;
    },

    async submitVoucherForm() {
      if (!this.voucherTarget) return;
      const balance = Number(this.voucherForm.premiumVoucherBalance);
      if (!Number.isInteger(balance) || balance < 0) {
        this.$store.dispatch("showSnack", {
          message: "Informe uma quantidade válida de vouchers.",
          color: "error",
        });
        return;
      }
      this.savingVouchers = true;
      try {
        await new UpdateFranchiseAssociateVouchers().execute(this.voucherTarget.id, {
          hasPremiumVoucher: this.voucherForm.hasPremiumVoucher,
          premiumVoucherBalance: this.voucherForm.hasPremiumVoucher ? balance : 0,
        });
        this.$store.dispatch("showSnack", {
          message: "Vouchers atualizados com sucesso.",
          color: "success",
        });
        this.closeVoucherDialog();
        await this.loadList();
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Não foi possível atualizar os vouchers.",
          color: "error",
        });
      } finally {
        this.savingVouchers = false;
      }
    },

    openDialog() {
      this.resetForm();
      (this.$refs.formRef as Vue & { resetValidation?: () => void })?.resetValidation?.();
      this.dialogOpen = true;
    },

    closeDialog() {
      this.dialogOpen = false;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        name: "",
        document: "",
        email: "",
        phone: "",
      };
    },

    normalizeDigits(v: string) {
      return String(v || "").replace(/\D/g, "");
    },

    extractErrorMessage(error: unknown): string {
      const response = (error as {
        response?: {
          status?: number;
          data?: { message?: string | string[] } | string;
        };
      })?.response;

      const status = response?.status;
      const responseData = response?.data;
      let responseMessage = "";

      if (typeof responseData === "string") {
        responseMessage = responseData;
      } else if (responseData && Array.isArray(responseData.message)) {
        responseMessage = responseData.message.join(" ");
      } else if (responseData && typeof responseData.message === "string") {
        responseMessage = responseData.message;
      }

      if (
        status === 409 ||
        responseMessage.includes("Franchise associate already exists") ||
        responseMessage.includes("Franchise user already exists")
      ) {
        return "Já existe um associado cadastrado com o CPF/CNPJ informado.";
      }

      return "Não foi possível cadastrar o associado.";
    },

    async submitForm() {
      const formRef = this.$refs.formRef as Vue & { validate?: () => boolean };
      if (formRef.validate && !formRef.validate()) return;
      this.saving = true;
      try {
        const reg = new RegisterFranchiseAssociate();
        await reg.execute({
          name: this.form.name.trim(),
          phone: this.normalizeDigits(this.form.phone),
          email: this.form.email.trim(),
          document: this.normalizeDigits(this.form.document),
        });
        this.$store.dispatch("showSnack", {
          message: "Associado cadastrado. Senha enviada por e-mail.",
          color: "success",
          timer: 5000,
        });
        this.closeDialog();
        await this.loadData();
      } catch (error: unknown) {
        this.$store.dispatch("showSnack", {
          message: this.extractErrorMessage(error),
          color: "error",
          timer: 4000,
        });
      } finally {
        this.saving = false;
      }
    },

    canInactivate(item: FranchiseAssociateListItem): boolean {
      return item.status === "ACTIVE" || item.status === "PENDING";
    },

    canActivate(item: FranchiseAssociateListItem): boolean {
      return item.status === "INACTIVE";
    },

    statusColor(status: string) {
      if (status === "INACTIVE") return "grey";
      if (status === "PENDING") return "amber darken-2";
      return "green darken-2";
    },

    statusLabel(status: string) {
      const map: Record<string, string> = {
        ACTIVE: "Ativo",
        PENDING: "Pendente",
        INACTIVE: "Inativo",
      };
      return map[status] || status;
    },

    resolveCareerRole(item: FranchiseAssociateListItem) {
      if (item.careerRole) return item.careerRole;
      const transactionPercent = Number(item.transactionPercent || 0);
      const signaturePercent = Number(item.signaturePercent || 0);
      if (transactionPercent === 0.4 && signaturePercent === 0.2) {
        return "EXPANSION_DIRECTOR";
      }
      if (transactionPercent === 0.3 && signaturePercent === 0.15) {
        return "EXPANSION_MANAGER";
      }
      if (transactionPercent === 0.2 && signaturePercent === 0.1) {
        return "ASSOCIATE_MANAGER";
      }
      if (transactionPercent === 0.1 && signaturePercent === 0.05) {
        return "ASSOCIATE";
      }
      return "UNCLASSIFIED";
    },

    careerRoleLabel(item: FranchiseAssociateListItem) {
      if (item.careerRoleLabel) return item.careerRoleLabel;
      const labels: Record<string, string> = {
        ASSOCIATE: "Associado",
        ASSOCIATE_MANAGER: "Gestor de Associados",
        EXPANSION_MANAGER: "Gerente de Expansão",
        EXPANSION_DIRECTOR: "Diretor de Expansão",
        UNCLASSIFIED: "Sem cargo definido",
      };
      return labels[this.resolveCareerRole(item)] || "Sem cargo definido";
    },

    careerRoleColor(item: FranchiseAssociateListItem) {
      const colors: Record<string, string> = {
        ASSOCIATE: "blue darken-2",
        ASSOCIATE_MANAGER: "indigo darken-2",
        EXPANSION_MANAGER: "deep-purple darken-2",
        EXPANSION_DIRECTOR: "teal darken-2",
        UNCLASSIFIED: "grey",
      };
      return colors[this.resolveCareerRole(item)] || "grey";
    },

    formatDocument(doc: string) {
      const d = this.normalizeDigits(doc);
      if (d.length === 11) {
        return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (d.length === 14) {
        return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return doc;
    },

    phoneNumberFormat(phone: string) {
      const p = this.normalizeDigits(phone);
      if (p.length < 10) return phone;
      return `(${p.slice(0, 2)}) ${p.slice(2, p.length === 11 ? 7 : 6)}-${p.slice(p.length === 11 ? 7 : 6)}`;
    },

    formatDate(iso: string | null) {
      if (!iso) return "—";
      try {
        return format(new Date(iso), "dd/MM/yyyy HH:mm");
      } catch {
        return iso;
      }
    },

    openConfirmStatus(
      item: FranchiseAssociateListItem,
      status: "ACTIVE" | "INACTIVE"
    ) {
      this.pendingStatusChange = item;
      this.pendingStatus = status;
      this.confirmStatusOpen = true;
    },

    async confirmStatusChange() {
      if (!this.pendingStatusChange?.id || !this.pendingStatus) return;
      const pendingStatusChange = this.pendingStatusChange;
      const pendingStatus = this.pendingStatus;
      this.updatingStatus = true;
      try {
        const svc = new UpdateFranchiseAssociateStatus();
        await svc.execute(pendingStatusChange.id, pendingStatus);
        const isApproval =
          pendingStatus === "ACTIVE" && pendingStatusChange.status === "PENDING";
        this.$store.dispatch("showSnack", {
          message:
            pendingStatus === "ACTIVE"
              ? isApproval
                ? "Associado aprovado."
                : "Associado ativado."
              : "Associado inativado.",
          color: "success",
          timer: 4000,
        });
        this.confirmStatusOpen = false;
        this.pendingStatusChange = null;
        this.pendingStatus = null;
        await this.loadData();
      } catch {
        const isApproval =
          pendingStatus === "ACTIVE" && pendingStatusChange.status === "PENDING";
        this.$store.dispatch("showSnack", {
          message:
            pendingStatus === "ACTIVE"
              ? isApproval
                ? "Não foi possível aprovar."
                : "Não foi possível ativar."
              : "Não foi possível inativar.",
          color: "error",
          timer: 4000,
        });
      } finally {
        this.updatingStatus = false;
      }
    },
  },
});
</script>

<style scoped>
.associates-page__table-card,
.associates-page__dialog-card {
  overflow: hidden;
}

.associates-page__table {
  margin-top: 8px;
}

.associates-page__action {
  border-radius: 999px;
}

.associates-page__view-toggle {
  border-radius: 999px;
  overflow: hidden;
}

.metric-card__label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 4px;
}

.metric-card__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a237e;
}

.metric-card__value--premium {
  color: #2e7d32;
}

.actions {
  white-space: nowrap;
}

.voucher-modal {
  border-radius: 16px !important;
  overflow: hidden;
}

.voucher-modal__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #4527a0 0%, #6a1b9a 55%, #8e24aa 100%);
  color: #fff;
}

.voucher-modal__hero-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.voucher-modal__close {
  flex-shrink: 0;
}

.voucher-modal__hero-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.voucher-modal__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.voucher-modal__subtitle {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.35;
  opacity: 0.9;
}

.voucher-modal__body {
  padding: 18px 22px 14px !important;
}

.voucher-modal__associate {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  border-radius: 12px;
  background: #f8f6fc;
  border: 1px solid #ece6f5;
}

.voucher-modal__avatar-text {
  color: #4527a0;
  font-weight: 700;
  font-size: 0.95rem;
}

.voucher-modal__associate-info {
  min-width: 0;
  flex: 1;
}

.voucher-modal__associate-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1a1a2e;
  line-height: 1.3;
}

.voucher-modal__associate-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 0.78rem;
  color: #666;
  line-height: 1.35;
}

.voucher-modal__notice {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 14px;
  border-radius: 8px;
  font-size: 0.8rem;
  line-height: 1.35;
}

.voucher-modal__notice--warning {
  background: #fff8e1;
  color: #6d4c00;
  border: 1px solid #ffe082;
}

.voucher-modal__notice--success {
  background: #e8f5e9;
  color: #1b5e20;
  border: 1px solid #a5d6a7;
}

.voucher-modal__controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 12px;
}

.voucher-modal__section {
  display: flex;
  flex-direction: column;
  min-height: 148px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #ececec;
  background: #fff;
}

.voucher-modal__section--balance-inactive {
  background: #fafafa;
  border-style: dashed;
}

.voucher-modal__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.voucher-modal__section-title {
  font-weight: 700;
  font-size: 0.9rem;
  color: #1a1a2e;
}

.voucher-modal__section-hint {
  font-size: 0.78rem;
  color: #777;
  line-height: 1.4;
}

.voucher-modal__balance-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 8px;
}

.voucher-modal__balance-input {
  max-width: 96px;
  flex: 0 0 96px;
}

.voucher-modal__balance-input >>> input {
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
}

.voucher-modal__balance-summary {
  margin-top: 8px;
  text-align: center;
  font-size: 0.78rem;
  color: #555;
}

.voucher-modal__balance-placeholder {
  margin: auto 0 0;
  padding-top: 12px;
  font-size: 0.78rem;
  color: #999;
  text-align: center;
}

.voucher-modal__footnote {
  font-size: 0.75rem;
  color: #888;
  line-height: 1.4;
}

.voucher-modal__actions {
  padding: 10px 20px 14px !important;
  background: #fff;
}

@media (max-width: 700px) {
  .voucher-modal__controls {
    grid-template-columns: 1fr;
  }

  .voucher-modal__section {
    min-height: auto;
  }
}
</style>
